import { useNavigate, useLocation, Routes, Route } from "react-router-dom";
import React, { useState, useEffect, useCallback, useRef } from "react";
import menu from "../../assets/menu.svg";
import MenuItem from "./menuItem.js";
import MenuContent from "./menuContent.js";
import DishDetailView from "../../components/Menu/DishDetailView.js";
import DishInfoModal from "../../components/Modal/DishInfoModal.js";
import { getDishById, exportDishesToExcel, deleteDish, updateDishStatus } from "../../services/menu-service.js";
import MenuImportModal from "../../components/Modal/MenuImportModal.js";
import { useAuth } from "../../utils/authContext.js";
import { useMenu } from "../../hooks/useMenu.js";
import { usePagination } from "../../hooks/usePagination.js";
import Pagination from "../../components/comman/pagination.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import Button from "../../components/comman/button.js";
import DishAdditionsModal from "../../components/Modal/DishAdditionsModal.js";
import ConfirmationModal from "../../components/Modal/ConfirmationModal.jsx";
import Toast from "../../components/Toast/toast.js";
import ErrorDisplay from "../../components/comman/ErrorDisplay.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import { normalizeRowsPerPage } from "../../constants/pagination.js";
import RowsPerPageSelect from "../../components/comman/RowsPerPageSelect.js";
import { useSearch } from "../../hooks/useSearch.js";

const readStoredMenuPageSize = () =>
    normalizeRowsPerPage(localStorage.getItem("menuItemsPerPage"));

const Menu = () => {
    const [selectedDish, setSelectedDish] = useState(null);
    const [isAdditionsModalVisible, setAdditionsModalVisible] = useState(false);
    const [isConfirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
    const [isDishInfoModalVisible, setDishInfoModalVisible] = useState(false);
    const [dishForModal, setDishForModal] = useState(null);
    const [refreshAdditionsFlag, setRefreshAdditionsFlag] = useState(false);
    const [loadingAnimation, setLoadingAnimation] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [viewMode, setViewMode] = useState(() => localStorage.getItem("menuViewMode") || "grid");
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [selectedDishIds, setSelectedDishIds] = useState(new Set());
    const [isConfirmBulkDeleteVisible, setIsConfirmBulkDeleteVisible] = useState(false);
    const [isConfirmBulkStatusVisible, setIsConfirmBulkStatusVisible] = useState(false);
    const [pendingStatus, setPendingStatus] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isStatusUpdating, setIsStatusUpdating] = useState(false);
    const [showActiveOnly, setShowActiveOnly] = useState(false);
    const toastRef = useRef(null);
    const dropdownRef = useRef(null);
    const { searchText, debouncedSearchText, setSearchText } = useSearch(400);

    const { chefData } = useAuth();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();

    const statusFilter = showActiveOnly ? "ACTIVE" : null;

    const {
        currentPage,
        setCurrentPage,
        itemsPerPage,
        changeItemsPerPage
    } = usePagination({
        initialPage: 1,
        initialItemsPerPage: readStoredMenuPageSize(),
        totalItems: 0
    });

    const { menuItems, totalItems: menuTotalItems, loading, error, fetchMenus } = useMenu({
        chefId: chefData?.id,
        itemsPerPage,
        statusFilter,
        searchQuery: debouncedSearchText,
        toastRef
    });

    const previousSearchRef = useRef(debouncedSearchText);

    // Fetch menus when page, page size, status filter, or search changes
    useEffect(() => {
        if (!chefData?.id) {
            return;
        }
        if (previousSearchRef.current !== debouncedSearchText) {
            previousSearchRef.current = debouncedSearchText;
            if (currentPage !== 1) {
                setCurrentPage(1);
                return;
            }
        }
        fetchMenus(currentPage);
    }, [chefData?.id, currentPage, fetchMenus, debouncedSearchText, setCurrentPage]);

    const handlePageSizeChange = useCallback(
        (size) => {
            changeItemsPerPage(size);
            localStorage.setItem("menuItemsPerPage", String(size));
        },
        [changeItemsPerPage]
    );

    const handleShowActiveOnlyChange = useCallback(
        (activeOnly) => {
            setShowActiveOnly(activeOnly);
            setCurrentPage(1);
        },
        [setCurrentPage]
    );

    // Handle modal closing and trigger additions refresh
    const handleAdditionsModalClose = useCallback(() => {
        setAdditionsModalVisible(false);
        setRefreshAdditionsFlag((prev) => !prev);
    }, []);

    const handlePageChange = useCallback((page) => {
        setLoadingAnimation(true);
        setCurrentPage(page);
        setTimeout(() => {
            setLoadingAnimation(false);
        }, 300);
    }, [setCurrentPage]);

    const handleAddNewDish = useCallback(() => {
        setSelectedDish(null);
        navigate("/menu/add");
    }, [navigate]);

    const handleDishCreated = useCallback(() => {
        fetchMenus(currentPage);
        navigate("/menu");
    }, [fetchMenus, currentPage, navigate]);

    const handleBackToList = useCallback(() => {
        navigate("/menu");
    }, [navigate]);

    const handleDishClick = useCallback((dish) => {
        setDishForModal(dish);
        setDishInfoModalVisible(true);
    }, []);

    // Memoize fetchMenus callback to prevent MenuItem re-renders
    const handleFetchMenus = useCallback(() => {
        fetchMenus(currentPage);
    }, [fetchMenus, currentPage]);

    const handleCloseDishInfoModal = useCallback(() => {
        setDishInfoModalVisible(false);
        setDishForModal(null);
    }, []);

    // Determine mode from URL
    const isEditMode = location.pathname.includes("/edit/") || location.pathname.includes("/add");
    const isDishViewMode = location.pathname.includes("/dish/");

    // Extract dish ID from URL
    const getDishIdFromUrl = useCallback(() => {
        const pathParts = location.pathname.split("/");
        return pathParts[pathParts.length - 1];
    }, [location.pathname]);

    // Fetch dish data when in edit/view mode
    useEffect(() => {
        const fetchDishData = async () => {
            if ((isEditMode || isDishViewMode) && (location.pathname.includes("/edit/") || location.pathname.includes("/dish/"))) {
                const dishId = getDishIdFromUrl();
                if (dishId && dishId !== "add" && dishId !== "ordering") {
                    try {
                        const dish = await getDishById(dishId);
                        setSelectedDish(dish);
                    } catch (error) {
                        console.error("Error fetching dish:", error);
                    }
                }
            } else if (location.pathname === "/menu/add") {
                setSelectedDish(null);
            }
        };

        fetchDishData();
    }, [location.pathname, isEditMode, isDishViewMode, getDishIdFromUrl]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener("mousedown", handleClickOutside);
        }

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isDropdownOpen]);

    const handleExport = useCallback(async () => {
        if (isExporting) return;
        setIsDropdownOpen(false);
        try {
            setIsExporting(true);
            const blob = await exportDishesToExcel();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `menu_export_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            toastRef.current?.addToast(
                getTranslation('menu.exportSuccess', language) || "Menu exported successfully",
                "success"
            );
        } catch (error) {
            toastRef.current?.addToast(
                getTranslation('menu.exportError', language) || "Failed to export menu",
                "error"
            );
        } finally {
            setIsExporting(false);
        }
    }, [isExporting, language]);

    // Selection mode handlers
    const handleToggleSelectionMode = useCallback(() => {
        setIsSelectionMode((prev) => {
            if (prev) {
                // When turning off selection mode, clear selections
                setSelectedDishIds(new Set());
            }
            return !prev;
        });
    }, []);

    const handleDishSelect = useCallback((dishId, isSelected) => {
        setSelectedDishIds((prev) => {
            const newSet = new Set(prev);
            if (isSelected) {
                newSet.add(dishId);
            } else {
                newSet.delete(dishId);
            }
            return newSet;
        });
    }, []);

    const handleSelectAll = useCallback(() => {
        const allIds = new Set(menuItems.map((item) => item.id));
        setSelectedDishIds(allIds);
    }, [menuItems]);

    const handleDeselectAll = useCallback(() => {
        setSelectedDishIds(new Set());
    }, []);

    const handleBulkStatusUpdate = useCallback(async (status) => {
        if (selectedDishIds.size === 0 || isStatusUpdating) return;

        setIsStatusUpdating(true);
        try {
            const statusPromises = Array.from(selectedDishIds).map((dishId) =>
                updateDishStatus(dishId, status)
            );

            await Promise.all(statusPromises);

            toastRef.current?.addToast(
                status === "ACTIVE"
                    ? (getTranslation('menu.bulkActivateSuccess', language) || "Ուտեստները հաջողությամբ ակտիվացվել են")
                    : (getTranslation('menu.bulkDeactivateSuccess', language) || "Ուտեստները հաջողությամբ ապաակտիվացվել են"),
                "success"
            );

            // Clear selection and exit selection mode
            setSelectedDishIds(new Set());
            setIsSelectionMode(false);
            setIsConfirmBulkStatusVisible(false);
            setPendingStatus(null);

            // Refresh menu list
            fetchMenus(currentPage);
        } catch (error) {
            toastRef.current?.addToast(
                getTranslation('menu.bulkStatusError', language) || "Չհաջողվեց թարմացնել ուտեստների կարգավիճակը",
                "error"
            );
        } finally {
            setIsStatusUpdating(false);
        }
    }, [selectedDishIds, isStatusUpdating, language, fetchMenus, currentPage]);

    const handleBulkDelete = useCallback(async () => {
        if (selectedDishIds.size === 0 || isDeleting) return;

        setIsDeleting(true);
        try {
            const deletePromises = Array.from(selectedDishIds).map((dishId) =>
                deleteDish(dishId)
            );

            await Promise.all(deletePromises);

            toastRef.current?.addToast(
                getTranslation('menu.dishDisabledSuccess', language) || "Կերակուրը հաջողությամբ անջատված է",
                "success"
            );

            // Clear selection and exit selection mode
            setSelectedDishIds(new Set());
            setIsSelectionMode(false);
            setIsConfirmBulkDeleteVisible(false);

            // Refresh menu list
            fetchMenus(currentPage);
        } catch (error) {
            toastRef.current?.addToast(
                getTranslation('menu.dishDisabledError', language) || "Չհաջողվեց անջատել կերակուրը",
                "error"
            );
        } finally {
            setIsDeleting(false);
        }
    }, [selectedDishIds, isDeleting, language, fetchMenus, currentPage]);

    return (
        <div className="transition-all duration-300 pl-2">
            <h1 className="text-xl font-bold mb-4 text-gray-900 dark:text-[#c9d1d9]">
                {getTranslation("menuItems.menu", language)}
            </h1>

            {error ? (
                <ErrorDisplay
                    error={error}
                    onRetry={() => fetchMenus(currentPage)}
                />
            ) : (
                <div style={{ opacity: loadingAnimation || loading ? 0.6 : 1 }}>
                    <Routes>
                        <Route
                            path="/"
                            element={
                                <>
                                    <div className="mb-4">
                                        <input
                                            type="text"
                                            name="dish-search"
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                            placeholder={
                                                getTranslation("menu.searchPlaceholder", language) ||
                                                "Search dishes in any language"
                                            }
                                            className="w-full px-3 sm:px-4 py-2.5 sm:py-2 text-sm sm:text-base bg-white dark:bg-[#0d1117] border border-gray-300 dark:border-[#30363d] rounded-lg focus:ring-2 focus:ring-gray-400 dark:focus:ring-[#484f58] focus:border-transparent text-gray-900 dark:text-[#c9d1d9] placeholder-gray-400 dark:placeholder-[#6e7681] font-medium"
                                            aria-label={
                                                getTranslation("menu.searchPlaceholder", language) ||
                                                "Search dishes in any language"
                                            }
                                        />
                                    </div>

                                    {/* Header Actions */}
                                    <div className="mb-6">
                                        {isSelectionMode ? (
                                            /* Selection Mode Toolbar */
                                            <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-lg p-4 shadow-sm">
                                                <div className="flex flex-wrap gap-4 items-center justify-between">
                                                    {/* Left side: Selection info and actions */}
                                                    <div className="flex flex-wrap items-center gap-3">
                                                        {/* Selection count badge */}
                                                        <div className="flex items-center gap-2">
                                                            <span className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-semibold bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200 border border-gray-300 dark:border-gray-700">
                                                                {selectedDishIds.size} {getTranslation("menu.itemsSelected", language)?.replace("{count}", "") || "selected"}
                                                            </span>
                                                        </div>

                                                        {/* Select All / Deselect All */}
                                                        <div className="flex gap-2">
                                                            {selectedDishIds.size < menuItems.length ? (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="md"
                                                                    onClick={handleSelectAll}
                                                                    className="font-medium"
                                                                >
                                                                    {getTranslation("menu.selectAll", language) || "Select All"}
                                                                </Button>
                                                            ) : (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="md"
                                                                    onClick={handleDeselectAll}
                                                                    className="font-medium"
                                                                >
                                                                    {getTranslation("menu.deselectAll", language) || "Deselect All"}
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>

                                                    {/* Right side: Actions */}
                                                    <div className="flex flex-wrap items-center gap-2 justify-end ml-auto">
                                                        {/* Status Update Buttons */}
                                                        {selectedDishIds.size > 0 && (
                                                            <>
                                                                <Button
                                                                    variant="primary"
                                                                    size="md"
                                                                    onClick={() => {
                                                                        setPendingStatus("ACTIVE");
                                                                        setIsConfirmBulkStatusVisible(true);
                                                                    }}
                                                                    disabled={isStatusUpdating || isDeleting}
                                                                    className="font-bold bg-green-600 hover:bg-green-700 border-none whitespace-nowrap px-3 sm:px-4"
                                                                >
                                                                    {isStatusUpdating && pendingStatus === "ACTIVE" ? (
                                                                        <>
                                                                            <LoadingSpinner size="small" />
                                                                            <span className="ml-2">{getTranslation("menu.activating", language) || "Activating..."}</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                                                            </svg>
                                                                            <span className="truncate">{getTranslation("menu.activateSelected", language) || "Activate"}</span>
                                                                        </>
                                                                    )}
                                                                </Button>
                                                                <Button
                                                                    variant="secondary"
                                                                    size="md"
                                                                    onClick={() => {
                                                                        setPendingStatus("DISABLED");
                                                                        setIsConfirmBulkStatusVisible(true);
                                                                    }}
                                                                    disabled={isStatusUpdating || isDeleting}
                                                                    className="font-bold whitespace-nowrap px-3 sm:px-4"
                                                                >
                                                                    {isStatusUpdating && pendingStatus === "DISABLED" ? (
                                                                        <>
                                                                            <LoadingSpinner size="small" />
                                                                            <span className="ml-2">{getTranslation("menu.deactivating", language) || "Deactivating..."}</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                                                            </svg>
                                                                            <span className="truncate">{getTranslation("menu.deactivateSelected", language) || "Deactivate"}</span>
                                                                        </>
                                                                    )}
                                                                </Button>
                                                            </>
                                                        )}

                                                        {/* Delete Selected Button */}
                                                        {selectedDishIds.size > 0 && (
                                                            <Button
                                                                variant="danger"
                                                                size="md"
                                                                onClick={() => setIsConfirmBulkDeleteVisible(true)}
                                                                disabled={isDeleting || isStatusUpdating}
                                                                className="font-bold whitespace-nowrap px-3 sm:px-4"
                                                            >
                                                                {isDeleting ? (
                                                                    <>
                                                                        <LoadingSpinner size="small" />
                                                                        <span className="ml-2">{getTranslation("menu.deleting", language) || "Deleting..."}</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <svg
                                                                            className="w-4 h-4 mr-1 sm:mr-2"
                                                                            fill="none"
                                                                            stroke="currentColor"
                                                                            viewBox="0 0 24 24"
                                                                            strokeWidth={2.5}
                                                                        >
                                                                            <path
                                                                                strokeLinecap="round"
                                                                                strokeLinejoin="round"
                                                                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                                            />
                                                                        </svg>
                                                                        <span className="truncate">{getTranslation("menu.deleteSelected", language) || "Delete"}</span>
                                                                    </>
                                                                )}
                                                            </Button>
                                                        )}

                                                        {/* Cancel Selection Mode */}
                                                        <Button
                                                            variant="secondary"
                                                            size="md"
                                                            onClick={handleToggleSelectionMode}
                                                            className="font-medium whitespace-nowrap px-3 sm:px-4"
                                                        >
                                                            {getTranslation("common.cancel", language) || "Cancel"}
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ) : (
                                            /* Normal Header Actions */
                                            <div className="flex flex-col sm:flex-row w-full gap-3 sm:gap-2 sm:items-center sm:justify-between">
                                                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:flex-wrap sm:gap-3 self-start sm:self-auto mr-auto w-full sm:w-auto">
                                                    {/* View Mode Toggle */}
                                                    <div className="flex bg-gray-100 dark:bg-[#161b22] p-1 rounded-lg border border-gray-200 dark:border-[#30363d]">
                                                        <button
                                                            type="button"
                                                            onClick={() => { setViewMode('grid'); localStorage.setItem('menuViewMode', 'grid'); }}
                                                            className={`p-1.5 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-white dark:bg-[#0d1117] shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}
                                                            title={getTranslation("menu.gridView", language) || "Grid View"}
                                                        >
                                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                                            </svg>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => { setViewMode('list'); localStorage.setItem('menuViewMode', 'list'); }}
                                                            className={`p-1.5 rounded-md transition-colors ${viewMode === 'list' ? 'bg-white dark:bg-[#0d1117] shadow-sm text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-900 dark:hover:text-white'}`}
                                                            title={getTranslation("menu.listView", language) || "List View"}
                                                        >
                                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                                                            </svg>
                                                        </button>
                                                    </div>

                                                    {/* All / Active filter */}
                                                    <div className="flex flex-col sm:flex-row sm:items-center gap-1.5 sm:gap-2">
                                                        <span className="text-xs font-medium text-gray-500 dark:text-[#8b949e] whitespace-nowrap">
                                                            {getTranslation("menu.showLabel", language) || "Show"}
                                                        </span>
                                                        <div className="flex bg-gray-100 dark:bg-[#161b22] p-1 rounded-lg border border-gray-200 dark:border-[#30363d]" role="group" aria-label={getTranslation("menu.showLabel", language) || "Show"}>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleShowActiveOnlyChange(false)}
                                                                className={`px-3 py-1 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${
                                                                    !showActiveOnly
                                                                        ? "bg-white dark:bg-[#0d1117] shadow-sm text-gray-900 dark:text-white"
                                                                        : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
                                                                }`}
                                                            >
                                                                {getTranslation("menu.filterAllDishes", language) || "All"}
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleShowActiveOnlyChange(true)}
                                                                className={`px-3 py-1 rounded-md text-sm font-semibold transition-colors whitespace-nowrap ${
                                                                    showActiveOnly
                                                                        ? "bg-white dark:bg-[#0d1117] shadow-sm text-gray-900 dark:text-white"
                                                                        : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
                                                                }`}
                                                            >
                                                                {getTranslation("menu.filterActiveOnly", language) || "Active"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 sm:items-center sm:justify-end w-full sm:w-auto mt-2 sm:mt-0">
                                                {/* Selection Mode Toggle */}
                                                <Button
                                                    variant="secondary"
                                                    size="md"
                                                    onClick={handleToggleSelectionMode}
                                                    className="w-full sm:w-auto font-bold"
                                                >
                                                    <svg
                                                        className="w-4 h-4 mr-2"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth={2.5}
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                    {getTranslation("menu.selectionMode", language) || "Selection Mode"}
                                                </Button>

                                                {/* Dropdown Menu for Secondary Actions */}
                                                <div className="relative" ref={dropdownRef}>
                                                    <Button
                                                        variant="secondary"
                                                        size="md"
                                                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                                        className="w-full sm:w-auto font-bold"
                                                    >
                                                        <svg
                                                            className="w-4 h-4 mr-2"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={2.5}
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                                                            />
                                                        </svg>
                                                        {getTranslation("menu.more", language) || "More"}
                                                        <svg
                                                            className={`w-4 h-4 ml-2 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                                                            fill="none"
                                                            stroke="currentColor"
                                                            viewBox="0 0 24 24"
                                                            strokeWidth={2.5}
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                d="M19 9l-7 7-7-7"
                                                            />
                                                        </svg>
                                                    </Button>

                                                    {isDropdownOpen && (
                                                        <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#161b22] rounded-md shadow-lg border border-gray-200 dark:border-[#30363d] z-50">
                                                            <div className="py-1">
                                                                <button
                                                                    onClick={handleExport}
                                                                    disabled={isExporting}
                                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-50 dark:hover:bg-[#1c2128] flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    {isExporting ? (
                                                                        <>
                                                                            <LoadingSpinner size="small" />
                                                                            <span className="ml-2">{getTranslation("menu.exporting", language) || getTranslation("menu.importStatus", language) || "Exporting..."}</span>
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <svg
                                                                                className="w-4 h-4 mr-2"
                                                                                fill="none"
                                                                                stroke="currentColor"
                                                                                viewBox="0 0 24 24"
                                                                                strokeWidth={2.5}
                                                                            >
                                                                                <path
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3"
                                                                                />
                                                                            </svg>
                                                                            {getTranslation("menu.export", language) || "Export"}
                                                                        </>
                                                                    )}
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        setIsImportModalOpen(true);
                                                                        setIsDropdownOpen(false);
                                                                    }}
                                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-50 dark:hover:bg-[#1c2128] flex items-center"
                                                                >
                                                                    <svg
                                                                        className="w-4 h-4 mr-2"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                        strokeWidth={2.5}
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                                                                        />
                                                                    </svg>
                                                                    {getTranslation("menu.import", language) || "Import"}
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        navigate("/configurations");
                                                                        setIsDropdownOpen(false);
                                                                    }}
                                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-50 dark:hover:bg-[#1c2128] flex items-center"
                                                                >
                                                                    <svg
                                                                        className="w-4 h-4 mr-2"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                        strokeWidth={2.5}
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                                                        />
                                                                    </svg>
                                                                    {getTranslation("menu.additions", language)}
                                                                </button>
                                                                <button
                                                                    onClick={() => {
                                                                        navigate("/menu/ordering");
                                                                        setIsDropdownOpen(false);
                                                                    }}
                                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-[#c9d1d9] hover:bg-gray-50 dark:hover:bg-[#1c2128] flex items-center"
                                                                >
                                                                    <svg
                                                                        className="w-4 h-4 mr-2"
                                                                        fill="none"
                                                                        stroke="currentColor"
                                                                        viewBox="0 0 24 24"
                                                                        strokeWidth={2.5}
                                                                    >
                                                                        <path
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                                                                        />
                                                                    </svg>
                                                                    {getTranslation("menu.ordering", language)}
                                                                </button>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Primary Action: Add Dish */}
                                                <Button
                                                    variant="primary"
                                                    size="md"
                                                    onClick={handleAddNewDish}
                                                    className="w-full sm:w-auto font-bold"
                                                >
                                                    <svg
                                                        className="w-4 h-4 mr-2"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth={2.5}
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                                        />
                                                    </svg>
                                                    {getTranslation("menu.addDish", language)}
                                                </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Menu Items Grid */}
                                    {loading && menuItems.length === 0 && !debouncedSearchText.trim() ? (
                                        <div className="flex justify-center items-center h-64">
                                            <LoadingSpinner />
                                        </div>
                                    ) : menuItems.length > 0 ? (
                                        <>
                                            <div className={viewMode === 'grid' ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6 items-stretch" : "flex flex-col gap-3 mb-6"}>
                                                {menuItems.map((item) => (
                                                    <MenuItem
                                                        key={item.id}
                                                        item={item}
                                                        viewMode={viewMode}
                                                        onDishClick={handleDishClick}
                                                        fetchMenus={handleFetchMenus}
                                                        toastRef={toastRef}
                                                        isSelectionMode={isSelectionMode}
                                                        isSelected={selectedDishIds.has(item.id)}
                                                        onSelectChange={handleDishSelect}
                                                    />
                                                ))}
                                            </div>

                                            {/* Rows per page + total + pagination (same as Clients) */}
                                            {menuTotalItems > 0 && (
                                                <div className="flex flex-col sm:flex-row justify-between items-center mt-6 mb-12 px-2">
                                                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-4 sm:mb-0">
                                                        <div className="mb-4 sm:mb-0">
                                                            <RowsPerPageSelect
                                                                label={
                                                                    getTranslation("common.rowsPerPage", language) ||
                                                                    "Rows per page"
                                                                }
                                                                value={itemsPerPage}
                                                                onChange={(value) => {
                                                                    handlePageSizeChange(value);
                                                                }}
                                                                margin=""
                                                            />
                                                        </div>
                                                        <span className="text-sm text-gray-500 dark:text-[#8b949e]">
                                                            {getTranslation("common.total", language)}: {menuTotalItems}
                                                        </span>
                                                    </div>
                                                    <Pagination
                                                        totalItems={menuTotalItems}
                                                        itemsPerPage={itemsPerPage}
                                                        currentPage={currentPage}
                                                        onPageChange={handlePageChange}
                                                    />
                                                </div>
                                            )}
                                        </>
                                    ) : (
                                        <div className="text-center py-12 px-4 bg-white dark:bg-[#161b22] rounded-lg shadow-sm dark:shadow-md border dark:border-[#30363d]">
                                            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white dark:bg-[#0d1117] mb-4 border dark:border-[#30363d]">
                                                <img src={menu} alt="Menu" className="w-8 h-8 opacity-50" />
                                            </div>
                                            <p className="text-base font-medium text-gray-500 dark:text-[#b1bac4]">
                                                {debouncedSearchText.trim()
                                                    ? (getTranslation("menu.noSearchResults", language) || "No dishes found")
                                                    : (getTranslation("menu.noDishes", language) || "No dishes found")}
                                            </p>
                                        </div>
                                    )}
                                </>
                            }
                        />

                        <Route
                            path="add"
                            element={
                                <>
                                    <div className="mb-4">
                                        <button
                                            className="text-gray-600 dark:text-[#b1bac4] font-bold text-sm bg-white dark:bg-[#161b22] px-4 py-2 rounded-md transition hover:bg-gray-50 dark:hover:bg-[#1c2128] hover:shadow-md border dark:border-[#30363d]"
                                            onClick={handleBackToList}
                                        >
                                            {getTranslation("menuPage.backToList", language)}
                                        </button>
                                    </div>
                                    <MenuContent
                                        chefId={chefData?.id}
                                        dishData={null}
                                        onDishCreated={handleDishCreated}
                                        handleAdditionsModalOpen={() => setAdditionsModalVisible(true)}
                                        refreshAdditionsFlag={refreshAdditionsFlag}
                                    />
                                </>
                            }
                        />

                        <Route
                            path="edit/:id"
                            element={
                                <>
                                    <div className="mb-4">
                                        <button
                                            className="text-gray-600 dark:text-[#b1bac4] font-bold text-sm bg-white dark:bg-[#161b22] px-4 py-2 rounded-md transition hover:bg-gray-50 dark:hover:bg-[#1c2128] hover:shadow-md border dark:border-[#30363d]"
                                            onClick={handleBackToList}
                                        >
                                            {getTranslation("menuPage.backToList", language)}
                                        </button>
                                    </div>
                                    {selectedDish ? (
                                        <MenuContent
                                            chefId={chefData?.id}
                                            dishData={selectedDish}
                                            onDishCreated={handleDishCreated}
                                            handleAdditionsModalOpen={() => setAdditionsModalVisible(true)}
                                            refreshAdditionsFlag={refreshAdditionsFlag}
                                        />
                                    ) : (
                                        <div className="flex justify-center items-center h-64">
                                            <LoadingSpinner />
                                        </div>
                                    )}
                                </>
                            }
                        />

                        <Route
                            path="dish/:id"
                            element={
                                <>
                                    <div className="mb-4">
                                        <Button
                                            variant="secondary"
                                            size="md"
                                            onClick={handleBackToList}
                                            className="font-bold"
                                        >
                                            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                            </svg>
                                            {getTranslation("menuPage.backToList", language)}
                                        </Button>
                                    </div>
                                    {selectedDish ? (
                                        <DishDetailView dish={selectedDish} />
                                    ) : (
                                        <div className="flex justify-center items-center h-64">
                                            <LoadingSpinner />
                                        </div>
                                    )}
                                </>
                            }
                        />

                        <Route path="ordering" element={<MenuItem />} />
                    </Routes>
                </div>
            )}

            {/* Modals */}
            {isAdditionsModalVisible && (
                <DishAdditionsModal closeModal={handleAdditionsModalClose} toastRef={toastRef} />
            )}

            {isConfirmDeleteVisible && (
                <ConfirmationModal
                    description={getTranslation("menu.confirmDelete", language) || "Are you sure you want to delete this dish?"}
                    onCancel={() => setConfirmDeleteVisible(false)}
                    onSubmit={() => {
                        // Handle delete
                        setConfirmDeleteVisible(false);
                    }}
                />
            )}

            {isConfirmBulkDeleteVisible && (
                <ConfirmationModal
                    description={getTranslation("menu.confirmBulkDelete", language) || "Միանգամից ջնջե՞լ նշված ուտեստները:"}
                    onCancel={() => setIsConfirmBulkDeleteVisible(false)}
                    onSubmit={handleBulkDelete}
                />
            )}

            {isConfirmBulkStatusVisible && (
                <ConfirmationModal
                    description={
                        pendingStatus === "ACTIVE"
                            ? (getTranslation("menu.confirmBulkActivate", language) || "Միանգամից ակտիվացնե՞լ նշված ուտեստները:")
                            : (getTranslation("menu.confirmBulkDeactivate", language) || "Միանգամից ապաակտիվացնե՞լ նշված ուտեստները:")
                    }
                    onCancel={() => {
                        setIsConfirmBulkStatusVisible(false);
                        setPendingStatus(null);
                    }}
                    onSubmit={() => handleBulkStatusUpdate(pendingStatus)}
                />
            )}

            {isDishInfoModalVisible && dishForModal && (
                <DishInfoModal
                    dish={dishForModal}
                    onClose={handleCloseDishInfoModal}
                />
            )}

            {/* Import Modal */}
            <MenuImportModal
                isOpen={isImportModalOpen}
                onClose={() => setIsImportModalOpen(false)}
                onImportComplete={() => {
                    setIsImportModalOpen(false);
                    fetchMenus(currentPage);
                }}
            />

            <Toast ref={toastRef} />
        </div>
    );
};

export default Menu;
